/**
 * @fileoverview Module for `src/lib/selectors.js`.
 */

import { useCallback } from 'react';

import identity from './functions/identity';

/**
 * Selector composition helpers for React hooks.
 */

/**
 * Returns a memoized selector that composes two selector functions.
 *
 * @param {Function[]} selectors - Two selectors `[first, second]`.
 * @returns {Function} Memoized composed selector.
 */
function useComposedSelector(selectors) {
  return useCallback(
    (collection) => selectors[1](selectors[0](collection)),
    [selectors],
  );
}

/**
 * Returns a memoized selector that sorts then transforms a collection.
 *
 * @param {Object} [params] - Selector options.
 * @param {Function} [params.selector=identity] - Output mapping function.
 * @param {Function} [params.sorter=identity] - Sort comparator.
 * @returns {Function} Memoized selector.
 */
function useSortedSelector({ selector = identity, sorter = identity } = { selector: identity, sorter: identity }) {
  return useCallback(
    (collection) => selector(collection.sort(sorter)),
    [selector, sorter],
  );
}

export { useComposedSelector, useSortedSelector };
